import type { AxiosInstance } from 'axios';
import type { paths } from '../schema';
import type { TimelineEntry } from '../types/timeline';
import type { RequestOptions, RequestOptionsWithParams } from './types';
import { curry1 } from './utils';

type CreateAutomationFolderResponse =
  paths['/api/automation2/folders']['post']['responses']['201']['content']['application/json'];

type GetAutomationResponse =
  paths['/api/automation2/automations/{automation_identifier}']['get']['responses']['200']['content']['application/json'];

type GetAutomationFoldersResponse =
  paths['/api/automation2/folders']['get']['responses']['200']['content']['application/json'];

type GetAutomationSubfoldersResponse =
  paths['/api/automation2/folders/{id}/subfolders']['get']['responses']['200']['content']['application/json'];

type PatchAutomationResponse =
  paths['/api/automation2/automations/{automation_identifier}']['patch']['responses']['200']['content']['application/json'];

type PatchAutomationFolderResponse =
  paths['/api/automation2/folders/{id}']['patch']['responses']['200']['content']['application/json'];

export type AutomationHistoryParams = Required<
  paths['/api/automation2/automations/{automation_identifier}/modification-history']['get']['parameters']
>['query'];

export type AutomationTimelineEntry = TimelineEntry<
  Record<string, any>,
  | 'automation_created'
  | 'automation_updated'
  | 'automation_deleted'
  | 'automation_started'
  | 'automation_paused'
  | 'automation_cancelled'
  | 'automation_resumed'
>;

export type AutomationHistory = Omit<
  paths['/api/automation2/automations/{automation_identifier}/modification-history']['get']['responses']['200']['content']['application/json'],
  'results'
> & {
  results: AutomationTimelineEntry[];
};

export const createAutomationFolder = async (
  instance: AxiosInstance,
  data: paths['/api/automation2/folders']['post']['requestBody']['content']['application/json'],
  options?: RequestOptions
): Promise<CreateAutomationFolderResponse> => {
  const res = await instance.post<CreateAutomationFolderResponse>(
    '/automation2/folders',
    data,
    options
  );

  return res.data;
};

export const deleteAutomationFolder = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptionsWithParams<
    paths['/api/automation2/folders/{id}']['delete']['parameters']['query']
  >
): Promise<void> => {
  await instance.delete(`/automation2/folders/${id}`, options);
};

export const getAutomation = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptions
): Promise<GetAutomationResponse> => {
  const res = await instance.get<GetAutomationResponse>(
    `/automation2/automations/${id}`,
    options
  );

  return res.data;
};

export const getAutomationFolders = async (
  instance: AxiosInstance,
  options?: RequestOptions
): Promise<GetAutomationFoldersResponse> => {
  const res = await instance.get<GetAutomationFoldersResponse>(
    '/automation2/folders',
    options
  );

  return res.data;
};

export const getAutomationHistory = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptionsWithParams<AutomationHistoryParams>
): Promise<AutomationHistory> => {
  const res = await instance.get<AutomationHistory>(
    `/automation2/automations/${id}/modification-history`,
    options
  );

  return res.data;
};

export const getAutomationSubfolders = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptionsWithParams<
    paths['/api/automation2/folders/{id}/subfolders']['get']['parameters']['query']
  >
): Promise<GetAutomationSubfoldersResponse> => {
  const res = await instance.get<GetAutomationSubfoldersResponse>(
    `/automation2/folders/${id}/subfolders`,
    options
  );

  return res.data;
};

export const patchAutomation = async (
  instance: AxiosInstance,
  id: string,
  data: Required<
    paths['/api/automation2/automations/{automation_identifier}']['patch']
  >['requestBody']['content']['application/json'],
  options?: RequestOptions
): Promise<PatchAutomationResponse> => {
  const res = await instance.patch<PatchAutomationResponse>(
    `/automation2/automations/${id}`,
    data,
    options
  );

  return res.data;
};

export const patchAutomationFolder = async (
  instance: AxiosInstance,
  id: string,
  data: Required<
    paths['/api/automation2/folders/{id}']['patch']
  >['requestBody']['content']['application/json'],
  options?: RequestOptions
): Promise<PatchAutomationFolderResponse> => {
  const res = await instance.patch<PatchAutomationFolderResponse>(
    `/automation2/folders/${id}`,
    data,
    options
  );

  return res.data;
};

export class AutomationService {
  public createAutomationFolder;
  public deleteAutomationFolder;
  public getAutomation;
  public getAutomationSubfolders;
  public patchAutomationFolder;
  public getAutomationHistory;

  constructor(private instance: AxiosInstance) {
    this.createAutomationFolder = curry1(createAutomationFolder)(this.instance);
    this.deleteAutomationFolder = curry1(deleteAutomationFolder)(this.instance);
    this.getAutomation = curry1(getAutomation)(this.instance);
    this.getAutomationSubfolders = curry1(getAutomationSubfolders)(
      this.instance
    );
    this.patchAutomationFolder = curry1(patchAutomationFolder)(this.instance);
    this.getAutomationHistory = curry1(getAutomationHistory)(this.instance);
  }
}
